import * as echarts from 'echarts/core';
import { BarChart, CustomChart } from 'echarts/charts';
import { AriaComponent, GridComponent, TooltipComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';

echarts.use([AriaComponent, BarChart, CustomChart, GridComponent, SVGRenderer, TooltipComponent]);

const initializedSections = new WeakMap();

const statusStyles = {
    overbilling: {
        color: '#F97316',
        fill: 'rgba(249, 115, 22, 0.14)',
        label: 'Overbilling',
    },
    underbilling: {
        color: '#60A5FA',
        fill: 'rgba(96, 165, 250, 0.2)',
        label: 'Underbilling',
    },
    'within-tolerance': {
        color: '#9CA3AF',
        fill: 'rgba(156, 163, 175, 0.16)',
        label: 'Within tolerance',
    },
};

const barFill = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: 'rgba(226, 232, 240, 0.01)' },
    { offset: 1, color: 'rgba(226, 232, 240, 0.08)' },
]);

const getSections = (scope = document) => {
    if (scope?.matches?.('[data-bar-graph]')) {
        return [scope];
    }

    return Array.from(scope?.querySelectorAll?.('[data-bar-graph]') || []);
};

const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
};

const wrapAnnotationText = (text, maxCharacters) => {
    const words = String(text).trim().split(/\s+/).filter(Boolean);
    let firstLine = '';
    let splitIndex = 0;

    words.some((word, index) => {
        const candidate = firstLine ? `${firstLine} ${word}` : word;

        if (firstLine && candidate.length > maxCharacters) {
            splitIndex = index;

            return true;
        }

        firstLine = candidate;
        splitIndex = index + 1;

        return false;
    });

    const truncate = (line) => line.length > maxCharacters
        ? `${line.slice(0, Math.max(maxCharacters - 1, 1)).trimEnd()}…`
        : line;
    const secondLine = words.slice(splitIndex).join(' ');

    return secondLine
        ? [truncate(firstLine), truncate(secondLine)]
        : [truncate(firstLine)];
};

const isValidPoint = (mode, point) => point
    && Number.isFinite(Number(point.value))
    && (mode === 'single' || Number.isFinite(Number(point.reference)));

const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
})[character]);

const deriveVariance = (point, tolerancePercent) => {
    const value = toNumber(point.value);
    const reference = toNumber(point.reference);
    const suppliedVariance = point.variance;
    const hasSuppliedVariance = suppliedVariance
        && typeof suppliedVariance === 'object'
        && statusStyles[suppliedVariance.status]
        && Number.isFinite(Number(suppliedVariance.from))
        && Number.isFinite(Number(suppliedVariance.to));

    if (hasSuppliedVariance) {
        return {
            status: suppliedVariance.status,
            from: toNumber(suppliedVariance.from),
            to: toNumber(suppliedVariance.to),
            isExplicit: true,
        };
    }

    const tolerance = Math.abs(reference) * (Math.max(toNumber(tolerancePercent), 0) / 100);
    const difference = value - reference;
    const status = Math.abs(difference) <= tolerance
        ? 'within-tolerance'
        : difference > 0
            ? 'overbilling'
            : 'underbilling';

    return {
        status,
        from: Math.min(value, reference),
        to: Math.max(value, reference),
        isExplicit: false,
    };
};

const getNumberFormatter = (locale, currency, compact = false) => {
    try {
        return new Intl.NumberFormat(locale || 'en-US', {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: compact ? 1 : 0,
            notation: compact ? 'compact' : 'standard',
            compactDisplay: 'short',
        });
    } catch {
        return new Intl.NumberFormat('en-US', {
            maximumFractionDigits: compact ? 1 : 0,
            notation: compact ? 'compact' : 'standard',
        });
    }
};

const getPlainNumberFormatter = (locale, compact = false) => new Intl.NumberFormat(locale || 'en-US', {
    maximumFractionDigits: 0,
    notation: compact ? 'compact' : 'standard',
    compactDisplay: 'short',
});

const getValueFormatter = (data, compact = false) => (
    data.mode === 'single'
        ? getPlainNumberFormatter(data.locale, compact)
        : getNumberFormatter(data.locale, data.currency, compact)
);

const getDisplayValue = (data, formatter, point) => (
    `${formatter.format(toNumber(point.value))}${data.mode === 'single' ? point.value_suffix || '' : ''}`
);

const getAxisMaximum = (data) => {
    const configuredMaximum = Number(data.axis?.max);

    if (Number.isFinite(configuredMaximum) && configuredMaximum > 0) {
        return configuredMaximum;
    }

    const highestValue = data.points.reduce((maximum, point) => Math.max(
        maximum,
        toNumber(point.value),
        toNumber(point.reference),
        toNumber(point.variance?.from),
        toNumber(point.variance?.to),
        toNumber(point.annotation?.value),
    ), 0);
    const target = Math.max(highestValue * 1.14, 1);
    const step = (10 ** Math.floor(Math.log10(target))) / 5;

    return Math.ceil(target / step) * step;
};

const getAxisBounds = (data) => {
    const minimum = Number.isFinite(Number(data.axis?.min)) ? Number(data.axis.min) : 0;
    const maximum = Math.max(getAxisMaximum(data), minimum + 1);
    const splitNumber = Math.max(Math.round(toNumber(data.axis?.split_number)) || 6, 1);

    return { minimum, maximum, splitNumber };
};

const getChartWidth = (viewport, data) => {
    const minimumPointWidth = data.mode === 'single' ? 176 : 90;

    return Math.max(viewport.clientWidth, data.points.length * minimumPointWidth);
};

const getCustomSeries = (points, tolerancePercent) => ({
    type: 'custom',
    coordinateSystem: 'cartesian2d',
    encode: {
        x: 0,
        y: 1,
        tooltip: [1, 2],
    },
    data: points.map((point, index) => ({
        value: [index, toNumber(point.value), toNumber(point.reference)],
        point,
        variance: deriveVariance(point, tolerancePercent),
    })),
    renderItem: (params, api) => {
        const point = points[params.dataIndex];

        if (!point) {
            return null;
        }

        const variance = deriveVariance(point, tolerancePercent);
        const pointIndex = api.value(0);
        const value = api.value(1);
        const reference = api.value(2);
        const zero = api.coord([pointIndex, 0]);
        const valueCoordinate = api.coord([pointIndex, value]);
        const referenceCoordinate = api.coord([pointIndex, reference]);
        const varianceStart = api.coord([pointIndex, variance.from]);
        const varianceEnd = api.coord([pointIndex, variance.to]);
        const categoryWidth = api.size([1, 0])[0];
        const bandWidth = Math.min(categoryWidth * 0.72, 80);
        const referenceLineWidth = bandWidth;
        const left = zero[0] - (bandWidth / 2);
        const top = Math.min(valueCoordinate[1], zero[1]);
        const bottom = Math.max(valueCoordinate[1], zero[1]);
        const varianceStyle = statusStyles[variance.status];
        const children = [
            {
                type: 'rect',
                shape: {
                    x: left,
                    y: top,
                    width: bandWidth,
                    height: Math.max(bottom - top, 1),
                },
                style: {
                    fill: barFill,
                    stroke: 'rgba(226, 232, 240, 0.28)',
                    lineWidth: 1,
                },
            },
            {
                type: 'line',
                shape: {
                    x1: zero[0] - (referenceLineWidth / 2),
                    y1: referenceCoordinate[1],
                    x2: zero[0] + (referenceLineWidth / 2),
                    y2: referenceCoordinate[1],
                },
                style: {
                    stroke: 'rgba(226, 232, 240, 0.72)',
                    lineWidth: 1.5,
                    lineDash: [5, 5],
                },
            },
        ];

        if (variance.isExplicit && variance.status !== 'within-tolerance') {
            children.push({
                type: 'rect',
                shape: {
                    x: left,
                    y: Math.min(varianceStart[1], varianceEnd[1]),
                    width: bandWidth,
                    height: Math.max(Math.abs(varianceStart[1] - varianceEnd[1]), 1),
                },
                style: {
                    fill: varianceStyle.fill,
                    stroke: varianceStyle.color,
                    lineWidth: 1,
                },
            });
        }

        if (point.annotation?.text) {
            const annotationValue = Number.isFinite(Number(point.annotation.value))
                ? toNumber(point.annotation.value)
                : value;
            const annotationCoordinate = api.coord([pointIndex, annotationValue]);
            const requestedMaxWidth = Number(point.annotation.max_width);
            const annotationMaxWidth = Number.isFinite(requestedMaxWidth) && requestedMaxWidth > 0
                ? Math.min(Math.max(requestedMaxWidth, 120), 240)
                : 240;
            const annotationMaxCharacters = Math.max(Math.floor((annotationMaxWidth - 20) / 8.5), 10);
            const annotationLines = wrapAnnotationText(point.annotation.text, annotationMaxCharacters);
            const annotationText = annotationLines.join('\n');
            const annotationWidth = annotationMaxWidth;
            const annotationHeight = Math.max(28, (annotationLines.length * 20) + 8);
            const markerCenterX = annotationCoordinate[0];
            const markerCenterY = Math.max(
                params.coordSys.y + 14,
                Math.min(annotationCoordinate[1], params.coordSys.y + params.coordSys.height - 14),
            );
            const annotationLeft = markerCenterX + 16;
            const flippedAnnotationLeft = markerCenterX - annotationWidth - 16;
            const safeAnnotationLeft = annotationLeft + annotationWidth <= params.coordSys.x + params.coordSys.width - 8
                ? annotationLeft
                : Math.max(params.coordSys.x + 8, flippedAnnotationLeft);
            const safeAnnotationTop = Math.max(
                params.coordSys.y + 8,
                Math.min(
                    markerCenterY - (annotationHeight / 2),
                    params.coordSys.y + params.coordSys.height - annotationHeight - 8,
                ),
            );

            children.push({
                type: 'group',
                children: [
                    {
                        type: 'rect',
                        shape: {
                            x: markerCenterX - 6,
                            y: markerCenterY - 6,
                            width: 12,
                            height: 12,
                            r: 3,
                        },
                        style: {
                            fill: 'rgba(14, 165, 233, 0.06)',
                            stroke: 'rgba(14, 165, 233, 0.55)',
                            lineWidth: 1,
                        },
                    },
                    {
                        type: 'circle',
                        shape: {
                            cx: markerCenterX,
                            cy: markerCenterY,
                            r: 2.5,
                        },
                        style: {
                            fill: 'rgba(14, 165, 233, 0.7)',
                            shadowBlur: 6,
                            shadowColor: '#0EA5E9',
                        },
                    },
                    {
                        type: 'rect',
                        shape: {
                            x: safeAnnotationLeft,
                            y: safeAnnotationTop,
                            width: annotationWidth,
                            height: annotationHeight,
                            r: 5,
                        },
                        style: {
                            fill: '#526070',
                        },
                    },
                    {
                        type: 'text',
                        clipPath: {
                            type: 'rect',
                            shape: {
                                x: safeAnnotationLeft + 10,
                                y: safeAnnotationTop + 4,
                                width: annotationWidth - 20,
                                height: annotationHeight - 8,
                            },
                        },
                        style: {
                            x: safeAnnotationLeft + 10,
                            y: safeAnnotationTop + (annotationHeight / 2),
                            text: annotationText,
                            fill: '#FFFFFF',
                            font: '16px Inter, sans-serif',
                            lineHeight: 20,
                            width: annotationWidth - 20,
                            overflow: 'truncate',
                            lineOverflow: 'truncate',
                            textVerticalAlign: 'middle',
                        },
                    },
                ],
            });
        }

        return {
            type: 'group',
            children,
        };
    },
});

const getSingleSeries = (data, formatter) => ({
    type: 'bar',
    data: data.points.map((point) => ({
        value: toNumber(point.value),
        point,
    })),
    barMaxWidth: 72,
    itemStyle: {
        color: '#B9D5E7',
        borderRadius: [3, 3, 0, 0],
    },
    label: {
        show: true,
        position: 'top',
        color: '#E2E8F0',
        fontFamily: 'Inter, sans-serif',
        fontSize: 13,
        formatter: ({ data: pointData }) => getDisplayValue(data, formatter, pointData?.point || {}),
    },
});

const getSeriesLabels = (data) => ({
    value: data.series?.value_label || 'Validated amount',
    reference: data.series?.reference_label || 'Invoiced amount',
    variance: data.series?.variance_label || 'Variance',
});

const getTooltipFormatter = (data, formatter, tolerancePercent) => {
    const tooltipMarkup = data.points.map((point) => {
        if (data.mode === 'single') {
            const popoverText = point.annotation_text || point.tooltip_description || '';

            return `
                <div class="bar-graph-tooltip bar-graph-tooltip--single">
                    <strong>${escapeHtml(point.label)}</strong>
                    ${point.secondary_label ? `<span class="bar-graph-tooltip__location">${escapeHtml(point.secondary_label)}</span>` : ''}
                    <span class="bar-graph-tooltip__value">${escapeHtml(data.series?.value_label || data.axis?.y_label || 'Value')}: ${escapeHtml(getDisplayValue(data, formatter, point))}</span>
                    ${popoverText ? `<span class="bar-graph-tooltip__copy">${escapeHtml(popoverText)}</span>` : ''}
                </div>
            `;
        }

        const labels = getSeriesLabels(data);
        const variance = deriveVariance(point, tolerancePercent);
        const difference = toNumber(point.value) - toNumber(point.reference);
        const varianceStyle = statusStyles[variance.status] || statusStyles['within-tolerance'];

        return `
            <div class="bar-graph-tooltip">
                <strong>${escapeHtml(point.label)}</strong>
                <span>${escapeHtml(labels.value)}: ${escapeHtml(formatter.format(toNumber(point.value)))}</span>
                <span>${escapeHtml(labels.reference)}: ${escapeHtml(formatter.format(toNumber(point.reference)))}</span>
                <span>${escapeHtml(labels.variance)} — ${escapeHtml(varianceStyle.label)}: ${escapeHtml(formatter.format(Math.abs(difference)))}</span>
            </div>
        `;
    });

    return (params) => tooltipMarkup[params.dataIndex] || '';
};

const buildOption = (data) => {
    const points = data.points;
    const { minimum, maximum, splitNumber } = getAxisBounds(data);
    const formatter = getValueFormatter(data);
    const tolerancePercent = toNumber(data.tolerance?.percent);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    return {
        animation: !reducedMotion,
        animationDuration: 500,
        animationEasing: 'cubicOut',
        aria: {
            enabled: true,
            description: `${data.title || 'Bar graph'}. ${data.axis?.x_label || 'Invoice cycle'} by ${data.axis?.y_label || 'Amount'}.`,
        },
        grid: {
            left: 0,
            right: 0,
            top: 16,
            bottom: data.mode === 'single' ? 82 : 56,
        },
        tooltip: {
            trigger: 'item',
            appendToBody: false,
            confine: true,
            enterable: false,
            backgroundColor: '#0E1015',
            borderColor: data.mode === 'single' ? '#B9D5E7' : '#F97316',
            borderWidth: 1,
            padding: [12, 14],
            showDelay: 0,
            hideDelay: 0,
            transitionDuration: 0,
            textStyle: {
                color: '#FFFFFF',
                fontFamily: 'Inter, sans-serif',
            },
            formatter: getTooltipFormatter(data, formatter, tolerancePercent),
        },
        xAxis: {
            type: 'category',
            data: points.map((point) => point.label),
            boundaryGap: true,
            axisLine: {
                lineStyle: {
                    color: 'rgba(226, 232, 240, 0.28)',
                },
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                color: '#E2E8F0',
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                margin: 14,
                interval: 0,
                width: data.mode === 'single' ? 144 : 82,
                overflow: 'truncate',
                ellipsis: '…',
                lineHeight: 18,
                formatter: (label, index) => data.mode === 'single'
                    ? `${label}\n${points[index]?.secondary_label || ''}`
                    : label,
            },
        },
        yAxis: {
            type: 'value',
            min: minimum,
            max: maximum,
            splitNumber,
            show: false,
            splitLine: {
                show: true,
                lineStyle: {
                    color: 'rgba(226, 232, 240, 0.18)',
                },
            },
        },
        series: [data.mode === 'single'
            ? getSingleSeries(data, formatter)
            : getCustomSeries(points, tolerancePercent)],
    };
};

const renderYAxis = (axisElement, data) => {
    const { minimum, maximum, splitNumber } = getAxisBounds(data);
    const compactFormatter = getValueFormatter(data, data.mode !== 'single');
    const fragment = document.createDocumentFragment();

    axisElement.replaceChildren();

    for (let index = 0; index <= splitNumber; index += 1) {
        const tick = document.createElement('span');
        const progress = index / splitNumber;
        const value = maximum - ((maximum - minimum) * progress);

        tick.className = 'bar-graph-sec__y-axis-tick';
        tick.style.top = `${progress * 100}%`;
        tick.textContent = compactFormatter.format(value);
        fragment.append(tick);
    }

    axisElement.append(fragment);
};

const getPointAnnouncement = (data, point) => {
    const formatter = getValueFormatter(data);

    if (data.mode === 'single') {
        const popoverText = point.annotation_text || point.tooltip_description || '';

        return [
            point.label,
            point.secondary_label,
            `${data.series?.value_label || data.axis?.y_label || 'Value'}: ${getDisplayValue(data, formatter, point)}`,
            popoverText,
        ].filter(Boolean).join('. ');
    }

    const labels = getSeriesLabels(data);
    const variance = deriveVariance(point, data.tolerance?.percent);
    const status = statusStyles[variance.status] || statusStyles['within-tolerance'];

    return `${point.label}. ${labels.value}: ${formatter.format(toNumber(point.value))}. ${labels.reference}: ${formatter.format(toNumber(point.reference))}. ${labels.variance}: ${status.label}.`;
};

const scrollPointIntoView = (state, index) => {
    const pointWidth = state.chartElement.clientWidth / state.data.points.length;
    const targetLeft = ((index + 0.5) * pointWidth) - (state.viewport.clientWidth / 2);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    state.viewport.scrollTo({
        left: Math.max(targetLeft, 0),
        behavior: reducedMotion ? 'auto' : 'smooth',
    });
};

const parseData = (section) => {
    const dataElement = section.querySelector('[data-bar-graph-data]');

    if (!dataElement?.textContent) {
        return null;
    }

    try {
        const data = JSON.parse(dataElement.textContent);
        const mode = data?.mode === 'single' ? 'single' : 'comparison';
        const points = Array.isArray(data?.points) ? data.points.filter((point) => isValidPoint(mode, point)) : [];

        return points.length ? { ...data, mode, points } : null;
    } catch {
        return null;
    }
};

const initBarGraphSections = (scope = document) => {
    getSections(scope).forEach((section) => {
        if (initializedSections.has(section)) {
            return;
        }

        const data = parseData(section);
        const visual = section.querySelector('.bar-graph-sec__visual');
        const viewport = section.querySelector('[data-bar-graph-viewport]');
        const chartElement = section.querySelector('[data-bar-graph-chart]');
        const axisElement = section.querySelector('[data-bar-graph-y-axis]');
        const liveElement = section.querySelector('[data-bar-graph-live]');

        if (!data || !visual || !viewport || !chartElement || !axisElement) {
            return;
        }

        const chart = echarts.init(chartElement, null, {
            renderer: 'svg',
        });
        const state = {
            chart,
            data,
            visual,
            viewport,
            chartElement,
            liveElement,
            frame: null,
            activeIndex: 0,
        };
        const resizeChart = () => {
            if (state.frame) {
                return;
            }

            state.frame = window.requestAnimationFrame(() => {
                state.frame = null;
                const chartWidth = getChartWidth(viewport, data);
                const chartHeight = Math.max(chartElement.clientHeight, 360);
                const isScrollable = chartWidth > viewport.clientWidth + 1;
                const renderedChartWidth = isScrollable ? chartWidth : chartWidth + 18;

                chartElement.style.width = `${renderedChartWidth}px`;
                chart.resize({
                    width: renderedChartWidth,
                    height: chartHeight,
                });
                chart.setOption(buildOption(data), true);
                visual.classList.toggle('is-scrollable', isScrollable);
            });
        };
        const showPoint = (index) => {
            const pointsCount = data.points.length;

            if (!pointsCount) {
                return;
            }

            state.activeIndex = (index + pointsCount) % pointsCount;
            chart.dispatchAction({
                type: 'showTip',
                seriesIndex: 0,
                dataIndex: state.activeIndex,
            });
            scrollPointIntoView(state, state.activeIndex);

            if (liveElement) {
                liveElement.textContent = getPointAnnouncement(data, data.points[state.activeIndex]);
            }
        };

        initializedSections.set(section, state);
        renderYAxis(axisElement, data);
        resizeChart();

        chart.on('click', (params) => {
            if (Number.isInteger(params.dataIndex)) {
                showPoint(params.dataIndex);
            }
        });

        chartElement.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                showPoint(state.activeIndex + 1);
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                showPoint(state.activeIndex - 1);
            }

            if (event.key === 'Escape') {
                chart.dispatchAction({ type: 'hideTip' });
            }
        });

        if (typeof ResizeObserver === 'function') {
            const resizeObserver = new ResizeObserver(resizeChart);
            resizeObserver.observe(viewport);
            state.resizeObserver = resizeObserver;
        } else {
            window.addEventListener('resize', resizeChart, { passive: true });
        }
    });
};

initBarGraphSections();
window.initBarGraphSections = initBarGraphSections;
